import { useState, useMemo } from "react";


export default function LoanCalculator() {
  const [loanAmount, setLoanAmount] = useState(150000);
  const [tenure, setTenure] = useState(12);
  const interestRate = 14.99; // p.a. as seen in image
  const processingFee = 999;

  const emi = useMemo(() => {
    const p = loanAmount;
    const r = interestRate / 12 / 100;
    const n = tenure;
    if (r === 0) return p / n;
    return (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  }, [loanAmount, tenure]);

  const totalInterest = useMemo(() => {
    return (emi * tenure) - loanAmount;
  }, [emi, tenure, loanAmount]);

  const websiteLink = import.meta.env.VITE_WEBSITE_LINK;
  return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="mb-8 text-center md:text-left">
          <h1 className="text-2xl md:text-4xl font-bold font-serif mb-4 leading-tight">
            Know your repayment before you borrow.
          </h1>
          <p className="text-md md:text-xl font-semibold max-w-2xl">
            Adjust amount and tenure. See your EMI instantly. No surprises later.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="bg-white rounded-3xl border border-[var(--secondary)]/10 p-6 md:p-8 lg:p-10 shadow-[0_15px_40px_rgba(0,0,0,0.02)] flex flex-col lg:flex-row gap-8 lg:gap-12 relative overflow-hidden font-roboto">
          {/* Left Side: Interaction */}
          <div className="flex-1 py-2">
            <div className="mb-10">
              <h2 className="text-xl md:text-3xl font-bold font-sans text-[var(--secondary)] mb-3">Calculate Your EMI</h2>
              <p className="text-[var(--text-gray)] text-lg leading-relaxed max-w-xl opacity-80">
                Estimate your monthly repayments instantly. Select your loan amount and the tenure that suits your budget.
              </p>
            </div>

            <div className="space-y-12">
              {/* Loan Amount Slider */}
              <div className="space-y-6">
                <div className="flex justify-between items-center font-bold">
                  <span className="text-base text-[var(--text-primary)] opacity-80">Loan Amount</span>
                  <span className="text-xl text-[var(--secondary)]">₹{loanAmount.toLocaleString('en-IN')}</span>
                </div>
                <div className="relative h-1.5 flex items-center">
                  <input
                    type="range"
                    min="10000"
                    max="500000"
                    step="5000"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                    className="calculator-slider w-full h-1.5 rounded-full appearance-none cursor-pointer outline-none"
                    style={{
                      background: `linear-gradient(to right, var(--secondary) ${((loanAmount - 10000) / (500000 - 10000)) * 100}%, #E2E8F0 0%)`,
                    }}
                  />
                </div>
                <div className="flex justify-between text-[var(--text-gray-light)] text-xs font-bold opacity-70">
                  <span>₹10,000</span>
                  <span>₹5,00,000</span>
                </div>
              </div>

              {/* Tenure Slider */}
              <div className="space-y-6">
                <div className="flex justify-between items-center font-bold">
                  <span className="text-base text-[var(--text-primary)] opacity-80">Tenure (Months)</span>
                  <span className="text-xl text-[var(--secondary)]">{tenure} Months</span>
                </div>
                <div className="relative h-1.5 flex items-center">
                  <input
                    type="range"
                    min="3"
                    max="24"
                    step="1"
                    value={tenure}
                    onChange={(e) => setTenure(Number(e.target.value))}
                    className="calculator-slider w-full h-1.5 rounded-full appearance-none cursor-pointer outline-none"
                    style={{
                      background: `linear-gradient(to right, var(--secondary) ${((tenure - 3) / (24 - 3)) * 100}%, #E2E8F0 0%)`,
                    }}
                  />
                </div>
                <div className="flex justify-between text-[var(--text-gray-light)] text-xs font-bold opacity-70">
                  <span>3 Mo</span>
                  <span>24 Mo</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side: Result Sidebar */}
          <div className="lg:w-[380px] bg-[var(--secondary)] rounded-2xl p-8 text-white flex flex-col justify-center shadow-lg relative overflow-hidden">
            <div className="text-start py-4 relative ">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-4 opacity-80">Monthly EMI</p>
              <h3 className="text-5xl md:text-5xl font-black mb-2">₹{Math.round(emi).toLocaleString('en-IN')}</h3>
              <div className="w-12 h-1 bg-white/30 mx-auto rounded-full mt-4" />
            </div>

            <div className="space-y-4 pt-8 relative ">
              <div className="flex justify-between text-sm font-medium border-b border-white/10 pb-3">
                <span className="opacity-70">Interest Rate</span>
                <span className="font-bold">{interestRate.toFixed(2)}% p.a.</span>
              </div>
              <div className="flex justify-between text-sm font-medium border-b border-white/10 pb-3">
                <span className="opacity-70">Processing Fee</span>
                <span className="font-bold">₹{processingFee.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-sm font-medium border-b border-white/10 pb-3">
                <span className="opacity-70">Total Interest</span>
                <span className="font-bold">₹{Math.round(totalInterest).toLocaleString('en-IN')}</span>
              </div>
            </div>

            <div className="mt-8 relative ">
              <button
                onClick={() => {
                  window.open(websiteLink, "_blank");
                }}
                className="w-full py-4 bg-white text-[var(--secondary)] font-bold rounded-xl text-lg hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all duration-300">
                Check Eligibility
              </button>
            </div>

            {/* Decorative background circle */}
            <div className="absolute top-[-20%] right-[-20%] w-48 h-48 bg-white/5 rounded-full blur-3xl pointer-events-none" />
          </div>
        </div>

        {/* Supporting Info Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          {/* Example Card */}
          <div className="bg-[var(--secondary-accent)] rounded-2xl p-6 text-white flex flex-col items-center justify-center shadow-md relative overflow-hidden">
            <h3 className="text-xl md:text-2xl font-serif mb-1 text-center font-medium">Example: ₹10,000 Loan</h3>
            <h4 className="text-2xl mb-3 text-center">
              You receive: <span className="text-[var(--secondary)]">₹8,900</span>
            </h4>

            <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-4 text-center">
              (₹1,180 DEDUCTED UPFRONT)
            </p>

            <div className="w-full max-w-[240px] space-y-2 text-sm font-bold text-gray-300">
              <div className="flex items-center">
                <span className="w-1.5 h-1.5 bg-[var(--secondary)] rounded-full mr-2 shrink-0" />
                <span className="flex-1 opacity-90">Processing Fee</span>
                <span className="text-white ml-auto">₹1,000</span>
              </div>
              <div className="flex items-center">
                <span className="w-1.5 h-1.5 bg-[var(--secondary)] rounded-full mr-2 shrink-0" />
                <span className="flex-1 opacity-90">GST (18%)</span>
                <span className="text-white ml-auto">₹180</span>
              </div>
            </div>
          </div>

          {/* Transparency Table Card */}
          <div className="relative rounded-2xl p-6 shadow-sm border border-gray-100 overflow-hidden bg-[#fafaf5]">
            <div
              className="absolute inset-0 z-0 pointer-events-none opacity-10"
              style={{
                backgroundImage: `url(/src/assets/tenurBgImage.png)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'grayscale(100%) sepia(10%) brightness(1.1)',
              }}
            />
            <div className="relative ">
              <h3 className="text-xl md:text-2xl font-serif font-bold mb-4">Transparency First</h3>

              <div className="space-y-2">
                <div className="flex justify-between items-center pb-2 border-b border-gray-200/50">
                  <span className="text-sm font-medium">APR</span>
                  <span className="font-bold text-sm">15% - 35%</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-gray-200/50">
                  <span className="text-sm font-medium">Processing Fee</span>
                  <span className="font-bold text-sm">Flat 10%</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-gray-200/50">
                  <span className="text-sm font-medium">GST on Fee</span>
                  <span className="font-bold text-sm">18%</span>
                </div>
                <div className="flex justify-between items-center pt-1">
                  <span className="text-sm font-medium">Hidden Charges</span>
                  <span className="font-bold text-green-700 text-base">Zero</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .calculator-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          background: white;
          border: 3px solid var(--secondary);
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
        }
        .calculator-slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
        }
      `}} />
    </div>
  );
}
